import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isValidCoordinates } from '@ma-js-common/map-helpers';

import BasicMap from 'components/BasicMap';
import {
    FRANCE_COORDINATES,
    KEEP_USER_ADDRESS_CHOICE,
    ZOOM_FOR_ADDRESS,
    ZOOM_FOR_CITY,
} from 'constants/Global';
import { deepEqual } from 'helpers/ObjectUtils';

import HouseMarker from './HouseMarker';

/**
 * Default zoom for map.
 * @type {Number}
 */
const DEFAULT_ZOOM = 5;

/**
 * Determine if marker should be rendered.
 * @param {Object} props Props of AddressMap component
 * @return {Boolean} True is marker should be shown, false otherwise
 */
function shouldShowMarker(props) {
    return (
        isValidCoordinates(props.values.coordinates) &&
        props.values._suggested_address !== KEEP_USER_ADDRESS_CHOICE
    );
}

/**
 * Adress Map component.
 */
export default class AddressMap extends Component {
    /**
     * Creates an instance of AddressMap.
     * @param {Object} props Given props.
     */
    constructor(props) {
        super(props);

        const { mapCoordinates } = props.address;

        /**
         * Store Mapbox map instance
         * @type {mapboxgl.Map}
         */
        this.map = null;

        /**
         * Update map coordinates
         */
        if (isValidCoordinates(mapCoordinates)) {
            const showMarker = shouldShowMarker(props);

            /**
             * React state
             * @type {{center: {lat: number, lng: number}, zoom: number, showMarker:boolean}}
             */
            this.state = {
                center: mapCoordinates,
                zoom: showMarker ? ZOOM_FOR_ADDRESS : ZOOM_FOR_CITY,
                showMarker,
            };
        } else {
            /**
             * React state
             * @type {{center: {lat: number, lng: number}, zoom: number}}
             */
            this.state = {
                center: FRANCE_COORDINATES,
                zoom: DEFAULT_ZOOM,
            };
        }

        this.onMapLoad = this.onMapLoad.bind(this);
        this.updateMap = this.updateMap.bind(this);
    }

    /**
     * React lifecycle.
     * Update value displayed if real value has changed.
     * @param {Object} prevProps Prev props of component
     */
    componentDidUpdate(prevProps) {
        const { address, values } = this.props;

        const mapCoordinatesChanged = !deepEqual(
            address.mapCoordinates,
            prevProps.address.mapCoordinates,
        );
        const formCoordinatesChanged = !deepEqual(values.coordinates, prevProps.values.coordinates);

        if (
            (isValidCoordinates(address.mapCoordinates) && mapCoordinatesChanged) ||
            formCoordinatesChanged
        ) {
            this.updateMap(address.mapCoordinates, shouldShowMarker(this.props));
        }
    }

    /**
     * Handle underlying map load.
     * @param  {Object} map Map loaded
     */
    // eslint-disable-next-line no-undef
    onMapLoad(map) {
        this.map = map;
        this.forceUpdate();
    }

    /**
     * Recenter the map and show a marker if wantend
     * @param {{lat: Number, lng: Number}} mapCoordinates
     * @param {Boolean} showMarker
     */
    updateMap(mapCoordinates, showMarker) {
        this.setState({
            center: mapCoordinates,
            zoom: showMarker ? ZOOM_FOR_ADDRESS : ZOOM_FOR_CITY,
            showMarker,
        });
    }

    /**
     * React lifecycle.
     */
    render() {
        const { zoom, center, showMarker } = this.state;

        let marker;
        if (showMarker && center && this.map) {
            marker = <HouseMarker map={this.map} coordinates={center} />;
        }

        return (
            <div className="address-map" data-step-address-map>
                {center && (
                    <BasicMap
                        style={{ height: '100%' }}
                        zoom={zoom}
                        coordinates={center}
                        onLoad={this.onMapLoad}
                        withInteraction={false}
                    >
                        {marker}
                    </BasicMap>
                )}
            </div>
        );
    }
}

AddressMap.propTypes = {
    address: PropTypes.shape({}),
    values: PropTypes.shape({}),
};
